/**
 * The pool as a picture (ADR 0088): a launch, a Prism-lit dot, drops into the top of a channel and
 * turns into the first lane that can run (in order), or splits across every lane that can, in
 * proportion to the headroom each has left (spread the load). Each account is a lane: a soft band
 * tinted with a Prism facet, its state a shape plus a label; a lane over its limit is dimmed with
 * a clock ring that fills toward its reset. Lanes are dragged (or moved with Alt and an arrow key)
 * to reorder, switched on and off to join or leave the pool, and an account outside the pool waits
 * below the divider, dimmed, to be dragged in.
 *
 * Motion (motion.md, the live layer): every dot is one finite WAAPI trip on transform and opacity,
 * started by a launch (a real one, or the demo's on ?mock=1) or by the person switching strategy;
 * never on mount, never under reduced motion (the duration tokens are 0), never while live is
 * paused or the tab is hidden. The lit path is the still twin.
 */
import './pool.css';
import { Clock, GripVertical, Plus } from '@orgus/ui/icons';
import { Reorder, useDragControls } from 'motion/react';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent, ReactNode } from 'react';
import type { LaunchPoolStrategy } from '@orgus/protocol';
import { Button, Skeleton, Switch, cn, formatClock, motionPresets } from '@orgus/ui';
import { Glyph } from './Glyph.tsx';
import { POOL_DIVIDER, limitProgress, moveAnnouncement, shareLabel, stepOrder, toggleOrder } from './pool.ts';
import type { LaneState, PoolLane, PoolPlan } from './pool.ts';

/** The trunk's x, the band's left edge (the gutter is pl-8, 32 px) and the elbow radius, in px. */
const TRUNK = 12;
const BAND = 32;
const ELBOW = 8;
/** At most this many dots in flight (motion.md L4: dropped, never queued). */
const MAX_DOTS = 6;

/** A launch to draw: `seq` changes once per launch; `to` is the lane a real launch ran on (null: as the plan says). */
export interface PoolLaunch {
  seq: number;
  to: string | null;
}

export interface PoolChannelProps {
  lanes: PoolLane[];
  plan: PoolPlan;
  strategy: LaunchPoolStrategy;
  now: number;
  /** The sortable order (members, the divider, the rest) after a drag, a key or a switch. */
  onOrder: (order: string[]) => void;
  launch?: PoolLaunch;
  /** No account at all yet: the one action. */
  onAdd?: () => void;
  loading?: boolean;
  className?: string;
}

// ── Geometry ─────────────────────────────────────────────────────────────────

interface Geom {
  entry: number;
  lanes: Record<string, number>;
}

/** The trunk from the entry down to a lane, turning into it with a rounded elbow. */
const run = (from: number, y: number) => `M ${TRUNK} ${from} L ${TRUNK} ${y - ELBOW} Q ${TRUNK} ${y} ${TRUNK + ELBOW} ${y} L ${BAND} ${y}`;
/** Only the turn into a lane. */
const branch = (y: number) => `M ${TRUNK} ${y - ELBOW} Q ${TRUNK} ${y} ${TRUNK + ELBOW} ${y} L ${BAND} ${y}`;

/** An element's top within the channel, ignoring transforms (a dragged lane's slot, not where the pointer holds it). */
function topWithin(el: HTMLElement, root: HTMLElement): number {
  let y = 0;
  let cur: HTMLElement | null = el;
  while (cur && cur !== root) {
    y += cur.offsetTop;
    cur = cur.offsetParent as HTMLElement | null;
  }
  return y;
}

const ms = (name: string, fallback: number): number => {
  if (typeof document === 'undefined') return 0;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (!v) return fallback;
  const n = Number.parseFloat(v);
  return v.endsWith('ms') ? n : n * 1000;
};

const livePaused = (): boolean => {
  try {
    return localStorage.getItem('orgus.live.paused') === 'true';
  } catch {
    return false;
  }
};

/** Points along a polyline at fractions of its length. */
function along(pts: [number, number][], t: number): [number, number] {
  const seg = pts.slice(1).map((p, i) => Math.hypot(p[0] - pts[i]![0], p[1] - pts[i]![1]));
  const total = seg.reduce((s, x) => s + x, 0) || 1;
  let d = t * total;
  for (let i = 0; i < seg.length; i++) {
    if (d <= seg[i]! || i === seg.length - 1) {
      const f = seg[i]! ? Math.min(1, d / seg[i]!) : 1;
      const a = pts[i]!;
      const b = pts[i + 1]!;
      return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
    }
    d -= seg[i]!;
  }
  return pts.at(-1)!;
}

// ── State shapes ─────────────────────────────────────────────────────────────

/** A lane's state as a 14 px shape: dot, half dot, dotted ring, clock ring, broken ring. */
function StateShape({ lane, now, next }: { lane: PoolLane; now: number; next: boolean }) {
  const s = lane.state;
  const c = 7;
  const r = 5;
  const circ = 2 * Math.PI * r;
  const shape: Record<LaneState, ReactNode> = {
    ready: <circle cx={c} cy={c} r={3.5} fill={next ? 'var(--brand-prism-1)' : 'currentColor'} />,
    partial: (
      <>
        <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeWidth={1.5} />
        <path d={`M ${c} ${c - 3} A 3 3 0 0 1 ${c} ${c + 3} Z`} fill="currentColor" />
      </>
    ),
    checking: <circle cx={c} cy={c} r={r} fill="none" stroke="currentColor" strokeWidth={1.5} strokeDasharray="1.5 2.5" strokeLinecap="round" />,
    limited: (
      <>
        <circle cx={c} cy={c} r={r} fill="none" stroke="var(--border-strong)" strokeWidth={1.5} />
        <circle
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="var(--status-attention)"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeDasharray={`${circ * (lane.limit ? limitProgress(lane.limit, now) : 0)} ${circ}`}
          transform={`rotate(-90 ${c} ${c})`}
        />
        <circle cx={c} cy={c} r={1} fill="var(--status-attention)" />
      </>
    ),
    'signed-out': (
      <>
        <path d={`M ${c + r} ${c} A ${r} ${r} 0 1 1 ${c} ${c - r}`} fill="none" stroke="var(--status-attention)" strokeWidth={1.5} strokeLinecap="round" />
        <path d={`M ${c} ${c - 2.5} L ${c} ${c + 0.5}`} stroke="var(--status-attention)" strokeWidth={1.5} strokeLinecap="round" />
      </>
    ),
    blocked: (
      <>
        <circle cx={c} cy={c} r={r} fill="none" stroke="var(--status-attention)" strokeWidth={1.5} />
        <path d={`M ${c - 2} ${c - 2} L ${c + 2} ${c + 2} M ${c + 2} ${c - 2} L ${c - 2} ${c + 2}`} stroke="var(--status-attention)" strokeWidth={1.5} strokeLinecap="round" />
      </>
    ),
  };
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden className="shrink-0 text-fg-subtle">
      {shape[s]}
    </svg>
  );
}

// ── One lane ─────────────────────────────────────────────────────────────────

interface LaneProps {
  lane: PoolLane;
  plan: PoolPlan;
  strategy: LaunchPoolStrategy;
  now: number;
  total: number;
  helpId: string;
  onToggle: (on: boolean) => void;
  onKey: (e: KeyboardEvent<HTMLLIElement>) => void;
  laneRef: (el: HTMLLIElement | null) => void;
  tintRef: (el: HTMLSpanElement | null) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

/** The tiny label at a lane's right: why it was passed over, its share, or that it takes the next launch. */
function laneTag(lane: PoolLane, plan: PoolPlan, strategy: LaunchPoolStrategy): { text: string; bright: boolean } | null {
  if (!lane.inPool) return null;
  if (strategy === 'spread') {
    const share = plan.shares[lane.id];
    if (share != null) return { text: lane.id === plan.next ? `Next, ${shareLabel(share)}` : shareLabel(share), bright: lane.id === plan.next };
    return { text: 'Left out', bright: false };
  }
  if (lane.id === plan.next) return { text: 'Next launch', bright: true };
  if (plan.passed.includes(lane.id)) return { text: 'Passed over', bright: false };
  return lane.canRun ? { text: 'Standby', bright: false } : { text: 'Passed over', bright: false };
}

function Lane({ lane, plan, strategy, now, total, helpId, onToggle, onKey, laneRef, tintRef, onDragStart, onDragEnd }: LaneProps) {
  const drag = useDragControls();
  const next = lane.inPool && lane.id === plan.next;
  const share = strategy === 'spread' ? plan.shares[lane.id] : undefined;
  const tag = laneTag(lane, plan, strategy);
  const where = lane.inPool ? `${lane.position} of ${total} in the pool` : 'not in the pool';
  return (
    <Reorder.Item
      as="li"
      value={lane.id}
      ref={laneRef}
      tabIndex={0}
      transition={motionPresets.move}
      dragListener={false}
      dragControls={drag}
      // A mouse or pen drags the whole band; a finger drags the grip, so the drawer still scrolls under it.
      onPointerDown={(e) => {
        if (e.pointerType !== 'touch') drag.start(e);
      }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onKeyDown={onKey}
      aria-describedby={helpId}
      aria-label={`${lane.name}, ${where}, ${lane.label}${tag ? `, ${tag.text.toLowerCase()}` : ''}`}
      data-hue={lane.hue}
      data-run={lane.canRun ? 'yes' : 'no'}
      data-out={lane.inPool ? undefined : ''}
      data-next={next ? '' : undefined}
      data-share={share != null ? '' : undefined}
      style={share != null ? ({ ['--share-wash' as string]: String(0.35 + 0.65 * share) } as CSSProperties) : undefined}
      className="pool-lane group ml-8 flex min-h-row-2line cursor-grab list-none items-center gap-2 rounded-card px-2 py-2 select-none active:cursor-grabbing"
    >
      <span className="pool-lane-lit" aria-hidden />
      <span className="pool-lane-ray" aria-hidden />
      <span className="pool-lane-tint" aria-hidden ref={tintRef} />
      <GripVertical
        className="pool-lane-grip relative size-4 shrink-0 touch-none text-fg-subtle"
        aria-hidden
        onPointerDown={(e) => {
          e.stopPropagation();
          drag.start(e);
        }}
      />
      <Glyph a={lane.account} className="pool-lane-glyph relative" />
      <span className="relative flex min-w-0 flex-1 flex-col">
        <span className="pool-lane-name flex min-w-0 items-center gap-2">
          {lane.inPool && <span className="shrink-0 text-caption text-fg-subtle num">{lane.position}</span>}
          <span className="min-w-0 truncate text-ui font-medium text-fg">{lane.name}</span>
        </span>
        <span className="flex min-w-0 items-center gap-1 text-caption text-fg-subtle">
          <StateShape lane={lane} now={now} next={next} />
          <span className={cn('max-w-full shrink-0 truncate', lane.canRun ? 'text-fg-muted' : 'text-fg-subtle')}>{lane.label}</span>
          {lane.meta && <span className="hidden min-w-0 truncate sm:inline">{`· ${lane.meta}`}</span>}
        </span>
      </span>
      {tag && <span className={cn('relative shrink-0 text-caption num', tag.bright ? 'font-medium text-accent-text' : 'text-fg-subtle')}>{tag.text}</span>}
      <span className="relative flex shrink-0 items-center pl-1" onPointerDown={(e) => e.stopPropagation()}>
        <Switch checked={lane.inPool} onCheckedChange={onToggle} aria-label={lane.inPool ? `Take ${lane.name} out of the pool` : `Add ${lane.name} to the pool`} />
      </span>
    </Reorder.Item>
  );
}

// ── The channel ──────────────────────────────────────────────────────────────

/** A quiet skeleton of the channel while the accounts load. */
function ChannelSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('relative flex flex-col gap-2 pl-8', className)} role="status" aria-label="Loading the pool">
      <span aria-hidden className="absolute inset-y-4 left-3 w-px bg-border" />
      <Skeleton className="h-control w-40" />
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-row-2line w-full rounded-card" />
      ))}
    </div>
  );
}

export function PoolChannel({ lanes, plan, strategy, now, onOrder, launch, onAdd, loading, className }: PoolChannelProps) {
  const uid = useId();
  const helpId = `${uid}-help`;
  const ray = `${uid}-ray`;
  const root = useRef<HTMLDivElement>(null);
  const entryRef = useRef<HTMLDivElement>(null);
  const dots = useRef<HTMLDivElement>(null);
  const laneEls = useRef(new Map<string, HTMLLIElement>());
  const tints = useRef(new Map<string, HTMLSpanElement>());
  const focusAfter = useRef<string | null>(null);
  const dragFrom = useRef<string[] | null>(null);
  const [geom, setGeom] = useState<Geom | null>(null);
  const [said, setSaid] = useState('');
  const [, setResized] = useState(0);

  const order = [...lanes.filter((l) => l.inPool).map((l) => l.id), POOL_DIVIDER, ...lanes.filter((l) => !l.inPool).map((l) => l.id)];
  const members = lanes.filter((l) => l.inPool);
  const byId = new Map(lanes.map((l) => [l.id, l]));

  // Measure where the entry and each member lane sit, after every commit (cheap: a handful of offsets).
  useLayoutEffect(() => {
    const el = root.current;
    const entry = entryRef.current;
    if (!el || !entry) return;
    const next: Geom = { entry: topWithin(entry, el) + entry.offsetHeight / 2, lanes: {} };
    for (const l of members) {
      const li = laneEls.current.get(l.id);
      if (li) next.lanes[l.id] = Math.round(topWithin(li, el) + li.offsetHeight / 2);
    }
    setGeom((g) => (g && JSON.stringify(g) === JSON.stringify(next) ? g : next));
  });
  useEffect(() => {
    const el = root.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setResized((n) => n + 1));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Keyboard moves keep focus on the lane that moved.
  useLayoutEffect(() => {
    const id = focusAfter.current;
    if (!id) return;
    focusAfter.current = null;
    laneEls.current.get(id)?.focus();
  });

  /** One launch: a dot (or a split of dots) down the trunk into its lane(s), then a tint where it lands. */
  const fly = (targets: { id: string; scale: number }[]) => {
    const layer = dots.current;
    const g = geom;
    if (!layer || !g || typeof document === 'undefined' || document.hidden || livePaused()) return;
    const flow = ms('--duration-flow', 1600);
    if (!flow) return;
    const highlight = ms('--duration-highlight', 600);
    // A calm, even speed: one --duration-flow per 160 px, so a split's dots ride the trunk together.
    const speed = 160 / flow;
    for (const t of targets) {
      const y = g.lanes[t.id];
      if (y == null || layer.childElementCount >= MAX_DOTS) continue;
      const pts: [number, number][] = [
        [TRUNK, g.entry],
        [TRUNK, y - ELBOW],
        [TRUNK + ELBOW * 0.3, y - ELBOW * 0.3],
        [TRUNK + ELBOW, y],
        [BAND + 24, y],
      ];
      const length = pts.slice(1).reduce((s, p, i) => s + Math.hypot(p[0] - pts[i]![0], p[1] - pts[i]![1]), 0);
      const dot = document.createElement('span');
      dot.className = 'pool-dot';
      layer.appendChild(dot);
      if (typeof dot.animate !== 'function') {
        dot.remove();
        continue;
      }
      const at = (f: number, opacity: number): Keyframe => {
        const [x, yy] = along(pts, f);
        return { offset: f, opacity, transform: `translate(${x}px, ${yy}px) scale(${t.scale})` };
      };
      const anim = dot.animate([at(0, 0), at(0.1, 1), at(0.5, 1), at(0.86, 1), at(1, 0)], { duration: length / speed, easing: 'linear', fill: 'forwards' });
      const done = () => dot.remove();
      anim.finished.then(() => {
        done();
        const tint = tints.current.get(t.id);
        if (tint && typeof tint.animate === 'function' && highlight) {
          tint.animate([{ opacity: 0 }, { opacity: 1, offset: 0.25 }, { opacity: 1, offset: 0.5 }, { opacity: 0 }], { duration: highlight * 2, easing: 'ease-out' });
        }
      }, done);
    }
  };

  const plan_ = useRef(plan);
  plan_.current = plan;
  const flyRef = useRef(fly);
  flyRef.current = fly;

  // A launch arrived: a real one names its lane; otherwise the plan says where it lands, or how it splits.
  const seen = useRef(launch?.seq ?? 0);
  useEffect(() => {
    if (!launch || launch.seq === seen.current) return;
    seen.current = launch.seq;
    const p = plan_.current;
    if (launch.to) flyRef.current([{ id: launch.to, scale: 1 }]);
    else if (p.strategy === 'spread') flyRef.current(Object.entries(p.shares).map(([id, share]) => ({ id, scale: 0.6 + 0.8 * share })));
    else if (p.next) flyRef.current([{ id: p.next, scale: 1 }]);
  }, [launch]);

  // The person switched strategy: show what that does, once.
  const lastStrategy = useRef(strategy);
  useEffect(() => {
    if (lastStrategy.current === strategy) return;
    lastStrategy.current = strategy;
    const p = plan_.current;
    if (strategy === 'spread') flyRef.current(Object.entries(p.shares).map(([id, share]) => ({ id, scale: 0.6 + 0.8 * share })));
    else if (p.next) flyRef.current([{ id: p.next, scale: 1 }]);
  }, [strategy]);

  if (loading) return <ChannelSkeleton className={className} />;

  const apply = (next: string[], id: string, focus: boolean) => {
    const name = byId.get(id)?.name ?? 'The account';
    const words = moveAnnouncement(order, next, id, name);
    if (words) setSaid(words);
    if (focus) focusAfter.current = id;
    onOrder(next);
  };
  const onKey = (id: string) => (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      apply(stepOrder(order, id, e.key === 'ArrowUp' ? -1 : 1), id, true);
      return;
    }
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const ids = order.filter((x) => x !== POOL_DIVIDER);
      const to = ids[ids.indexOf(id) + (e.key === 'ArrowUp' ? -1 : 1)];
      if (to) laneEls.current.get(to)?.focus();
      return;
    }
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      apply(toggleOrder(order, id, !byId.get(id)?.inPool), id, true);
    }
  };

  // Empty: no account at all, a quiet channel with its one action.
  if (!lanes.length) {
    return (
      <div className={cn('relative flex flex-col items-start gap-3 rounded-card border border-dashed border-border px-4 py-6', className)}>
        <svg width={48} height={40} viewBox="0 0 48 40" aria-hidden className="text-fg-faint">
          <circle cx={8} cy={8} r={4} className="pool-source" fill="var(--brand-prism-1)" opacity={0.5} />
          <path d="M 8 14 L 8 26 Q 8 32 14 32 L 44 32" fill="none" stroke="currentColor" strokeWidth={1.5} strokeDasharray="2 3" strokeLinecap="round" />
        </svg>
        <p className="text-caption text-fg-muted">A pool needs accounts to choose between.</p>
        {onAdd && (
          <Button size="sm" variant="secondary" icon={<Plus />} onClick={onAdd}>
            Add account
          </Button>
        )}
      </div>
    );
  }

  const ys = members.map((l) => geom?.lanes[l.id]).filter((y): y is number => y != null);
  const lastY = ys.at(-1);
  const open = members.filter((l) => l.canRun);
  const openYs = open.map((l) => geom?.lanes[l.id]).filter((y): y is number => y != null);
  const nextY = plan.next ? geom?.lanes[plan.next] : undefined;
  const refused = members.length > 0 && !open.length;
  const soonest = members
    .map((l) => l.limit?.until)
    .filter((t): t is number => t != null)
    .sort((a, b) => a - b)[0];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div ref={root} className="relative flex flex-col">
        {geom && (
          <svg aria-hidden className="pointer-events-none absolute inset-0 size-full overflow-visible">
            <defs>
              <linearGradient id={ray} gradientUnits="userSpaceOnUse" x1={0} y1={geom.entry} x2={0} y2={(lastY ?? geom.entry) + 1}>
                <stop offset="0" stopColor="var(--brand-prism-1)" />
                <stop offset="1" stopColor="var(--brand-prism-2)" />
              </linearGradient>
            </defs>
            {/* The channel itself: the trunk and a turn into every member lane. */}
            {lastY != null && <path className="pool-path" d={`M ${TRUNK} ${geom.entry} L ${TRUNK} ${lastY - ELBOW}`} stroke="var(--fg-faint)" strokeWidth={1.5} />}
            {members.map((l) => {
              const y = geom.lanes[l.id];
              if (y == null) return null;
              const passed = plan.passed.includes(l.id);
              return <path key={l.id} className="pool-path" d={branch(y)} stroke="var(--fg-faint)" strokeWidth={1.5} strokeDasharray={passed ? '2 3' : undefined} />;
            })}
            {refused && lastY != null && <path className="pool-path" d={`M ${TRUNK - 4} ${lastY + ELBOW} L ${TRUNK + 4} ${lastY + ELBOW}`} stroke="var(--status-attention)" strokeWidth={1.5} />}
            {/* In order: one lit path to the first lane that can run. */}
            <g className="pool-layer" style={{ opacity: strategy === 'in-order' ? 1 : 0 }}>
              {nextY != null && <path className="pool-path" d={run(geom.entry, nextY)} stroke={`url(#${ray})`} strokeWidth={2.5} opacity={0.9} />}
            </g>
            {/* Spread the load: a lit path into every lane that can run, as wide as its share. */}
            <g className="pool-layer" style={{ opacity: strategy === 'spread' ? 1 : 0 }}>
              {open.map((l) => {
                const y = geom.lanes[l.id];
                const share = plan.shares[l.id] ?? 0;
                if (y == null) return null;
                return <path key={l.id} className="pool-path" d={run(geom.entry, y)} stroke={`var(--brand-prism-${l.hue})`} strokeWidth={1 + 3 * share} opacity={0.4 + 0.5 * share} />;
              })}
            </g>
            {/* The entry: a ring holding the launch. */}
            <circle cx={TRUNK} cy={geom.entry} r={6} fill="var(--surface)" stroke="var(--border-strong)" strokeWidth={1.5} />
            <circle cx={TRUNK} cy={geom.entry} r={3} fill={`url(#${ray})`} opacity={refused ? 0.4 : 1} />
          </svg>
        )}
        <div ref={entryRef} className="flex h-control items-center gap-2 pl-8">
          <span className="text-caption text-fg-subtle">Launch</span>
          <span className={cn('min-w-0 truncate text-caption font-medium', refused ? 'text-status-attention' : 'text-fg')}>{plan.line}</span>
          {refused && soonest != null && (
            <span className="ml-auto flex shrink-0 items-center gap-1 text-caption text-fg-subtle num">
              <Clock className="size-3" aria-hidden />
              {`Until ${formatClock(soonest)}`}
            </span>
          )}
        </div>
        <Reorder.Group
          as="ol"
          axis="y"
          values={order}
          onReorder={(next: string[]) => onOrder(next)}
          className="flex flex-col gap-2 pt-1"
          aria-label="Pool lanes"
        >
          {!members.length && (
            <li className="ml-8 flex min-h-row-2line list-none items-center rounded-card border border-dashed border-border px-3 text-caption text-fg-subtle">Switch an account on, or drag one up, to add it</li>
          )}
          {order.map((id) => {
            if (id === POOL_DIVIDER) {
              const rest = lanes.length - members.length;
              return (
                <Reorder.Item key={id} as="li" value={id} dragListener={false} transition={motionPresets.move} className="flex h-control list-none items-center gap-2 pl-8" aria-hidden={rest ? undefined : true}>
                  <span className="text-caption text-fg-subtle">{rest ? 'Not in the pool' : 'Every account is in the pool'}</span>
                  <span aria-hidden className="h-px flex-1 border-t border-dashed border-border-strong" />
                </Reorder.Item>
              );
            }
            const lane = byId.get(id)!;
            return (
                <Lane
                  key={id}
                  lane={lane}
                  plan={plan}
                  strategy={strategy}
                  now={now}
                  total={members.length}
                  helpId={helpId}
                  onToggle={(on) => apply(toggleOrder(order, id, on), id, false)}
                  onKey={onKey(id)}
                  onDragStart={() => {
                    dragFrom.current = order;
                  }}
                  onDragEnd={() => {
                    const words = dragFrom.current ? moveAnnouncement(dragFrom.current, order, id, lane.name) : null;
                    dragFrom.current = null;
                    if (words) setSaid(words);
                  }}
                  laneRef={(el) => {
                    if (el) laneEls.current.set(id, el);
                    else laneEls.current.delete(id);
                  }}
                  tintRef={(el) => {
                    if (el) tints.current.set(id, el);
                    else tints.current.delete(id);
                  }}
                />
            );
          })}
        </Reorder.Group>
        <div ref={dots} aria-hidden className="pointer-events-none absolute inset-0 overflow-visible" />
      </div>
      <p id={helpId} className="pl-8 text-caption text-fg-subtle">
        Drag a lane, or press Alt with an arrow key, to change the order.
      </p>
      <p className="sr-only" role="status" aria-live="polite">
        {said}
      </p>
    </div>
  );
}

/** A launch that names no account, entering the pool when the switch is on. */
export function UnnamedLaunch({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <div className="pool-unnamed flex min-h-row-2line items-center gap-3 rounded-card px-3 py-2" data-on={on ? '' : undefined}>
      <span aria-hidden className="relative flex h-6 w-12 shrink-0 items-center">
        <span className="absolute inset-x-1 top-1/2 border-t border-dashed border-border-strong" />
        <span className="pool-unnamed-gate absolute right-0 flex flex-col gap-1">
          <span className="h-1 w-3 rounded-pill bg-(--brand-prism-1) opacity-70" />
          <span className="h-1 w-3 rounded-pill bg-(--brand-prism-2) opacity-60" />
          <span className="h-1 w-3 rounded-pill bg-(--brand-prism-5) opacity-50" />
        </span>
        <span className={cn('pool-unnamed-dot relative size-2.5 rounded-full border border-dashed', on ? 'border-fg-muted bg-surface' : 'border-fg-subtle bg-surface opacity-70')} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-ui text-fg">Launches that name no account</span>
        <span className="text-caption text-fg-subtle">{on ? 'Run from the pool when the project has no default' : 'Run as set on the machine'}</span>
      </span>
      <Switch checked={on} onCheckedChange={onChange} aria-label="Use the pool when a launch names no account" />
    </div>
  );
}

